import { Lightbulb, CheckCircle, AlertCircle, Clock, CreditCard } from 'lucide-react';
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";






import {
  resolveDisputedOffer,
  createRevisedOffer,
} from "../../../../../../../services/staffingService";

function formatJobDateTime(dateTime) {
  const date = new Date(dateTime);
  return date.toLocaleString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const disputeReasons = [
  { definition: "No-show for scheduled shift", status: 1 },
  { definition: "Late arrival to scheduled shift", status: 2 },
  { definition: "Missing checkout at shift end", status: 3 },
  { definition: "Late check-in for shift", status: 4 },
  { definition: "Check-in or check-out outside geofence area", status: 5 },
  { definition: "Inappropriate behavior during shift", status: 6 },
];

const WorkflowStepper = ({
  offer,
  negotiations,
  setOffer,
  setNegotiations,
  offerId,
  providerCompanyId,
  jobId,
}) => {
  const [dispute, setDispute] = useState(offer.dispute || {});
  const [customerRatePercentage, setCustomerRatePercentage] = useState(0.15);
  const [offerPrice, setOfferPrice] = useState(() => 
    offer?.jobId?.hourlyPayRate ? offer.jobId.hourlyPayRate * (1 - customerRatePercentage) : 0
  );

  // Update offer price when rate percentage changes
  useEffect(() => {
    if (offer?.jobId?.hourlyPayRate) {
      setOfferPrice(offer.jobId.hourlyPayRate * (1 - customerRatePercentage));
    }
  }, [offer, customerRatePercentage]);

  const handleDisputeChange = (status) => {
    const selectedReason = disputeReasons.find((r) => r.status === status);
    if (selectedReason) {
      const updatedDispute = {
        definition: selectedReason.definition,
        status: selectedReason.status,
        details: dispute.details || "",
        raisedAt: dispute.raisedAt || new Date(),
        resolvedBy: dispute.resolvedBy || null,
        resolvedAt: dispute.resolvedAt || null,
        resolutionNotes: dispute.resolutionNotes || "",
      };
      setDispute(updatedDispute);
    }
  };

  const resolveDispute = async () => {
    try {
      const disputeToResolve = dispute.status ? dispute : { ...offer.dispute, ...dispute };
      const resp = await resolveDisputedOffer(
        providerCompanyId,
        offerId,
        disputeToResolve
      );

      setOffer((prevOffer) => ({
        ...prevOffer,
        status: "resolved",
        dispute: resp.data.offer.dispute,
      }));
      toast.success("Dispute resolved successfully");
    } catch (error) {
      toast.error("Failed to resolve dispute. Server Error");
    }
  };

  const handleRevisedOffer = async () => {
    try {
      const revisedProviderHourlyRate = offerPrice;
      
      const { data } = await createRevisedOffer(
        providerCompanyId, 
        offer._id, 
        revisedProviderHourlyRate
      );
      
      setOffer(data.offer);
      if (data.offer.negotiations) {
        setNegotiations(data.offer.negotiations);
      }
      toast.success("Revised offer created successfully");
    } catch (error) {
      toast.error("Failed to create revised offer");
    }
  };

  const handlePriceChange = (value) => {
    setOfferPrice(value);
    if (offer?.jobId?.hourlyPayRate && value) {
      const newPercentage = (offer.jobId.hourlyPayRate - value) / offer.jobId.hourlyPayRate;
      setCustomerRatePercentage(newPercentage);
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber <= offer.overallActiveStep) {
      return 'border-blue-600 bg-blue-600 text-white';
    }
    return 'border-gray-300 bg-white text-gray-500';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="text-green-500" />;
      case 'disputed':
        return <AlertCircle className="text-orange-500" />;
      case 'resolved':
        return <CheckCircle className="text-blue-500" />;
      case 'rejected':
        return <AlertCircle className="text-red-500" />;
      case 'expired':
        return <Clock className="text-gray-500" />;
      default:
        return <Clock className="text-yellow-500" />;
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Progress Stepper */}
      <nav aria-label="Progress" className="flex-1 min-h-0 overflow-y-auto">
        <ol className="space-y-3 pb-4">
          
          {/* Step 1: Negotiation & Offer */}
          <li className="relative">
            <div className="flex items-start">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${getStepStatus(0)}`}>
                <span className="text-xs font-medium">1</span>
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  {getStatusIcon(offer.status)}
                  Offer & Negotiation
                </h3>
                
                {/* Main Offer Card */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-3">
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">Offer Details</h4>
                      <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        offer.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        offer.status === 'disputed' ? 'bg-orange-100 text-orange-800' :
                        offer.status === 'resolved' ? 'bg-blue-100 text-blue-800' :
                        offer.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        offer.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {offer.status}
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">
                          {Math.round(offer.negotiations?.customerRatePercentage || 0)}%
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Margin</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">
                          ${offer.negotiations?.providerHourlyRate || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Provider Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">
                          ${offer.jobId?.hourlyPayRate || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Customer Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-purple-600">
                          {offer.shifts?.length || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Shifts</div>
                      </div>
                    </div>
                    
                    {offer.negotiations?.createdAt && (
                      <div className="text-xs text-gray-500 border-t pt-2">
                        Created: {new Date(offer.negotiations.createdAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Cards - Only show when relevant */}
                {(offer.status === 'rejected' || offer.status === 'expired') && !offer.child?.exists && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center mb-3">
                      <Lightbulb className="text-amber-500 mr-2 text-base" />  
                      <h4 className="text-xs font-medium text-amber-800">Create Revised Offer</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-amber-700 min-w-[80px]">New Rate:</span>
                        <div className="relative flex-1 max-w-[80px]">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-amber-700 text-xs">$</span>
                          <input
                            type="number"
                            value={offerPrice}
                            onChange={(e) => handlePriceChange(parseFloat(e.target.value) || 0)}
                            className="form-input pl-5 pr-2 py-1 text-xs w-full border-amber-300 focus:border-amber-500 focus:ring-amber-200"
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded whitespace-nowrap">
                          {Math.round(((offer.jobId?.hourlyPayRate - offerPrice) / offer.jobId?.hourlyPayRate) * 100)}% margin
                        </span>
                      </div>
                      <button 
                        className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
                        onClick={handleRevisedOffer}
                      >
                        Create Revised Offer
                      </button>
                    </div>
                  </div>
                )}
                
                {offer.status === 'rejected' && offer.child?.exists && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Revised Offer Available</h4>
                    <p className="text-blue-700 mb-3 text-xs">A new offer has been created for this job with updated terms.</p>
                    <Link to={`/provider-company/${providerCompanyId}/jobs/${offer.jobId._id}/offer/${offer.child.childOfferId}`}>                                                    
                      <button className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                        View Active Offer →
                      </button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
            {/* Connecting line */}
            <div className="absolute left-4 top-8 -ml-px mt-0.5 h-full w-0.5 bg-gray-300" aria-hidden="true"></div>
          </li>

          {/* Step 2: Job Execution */}
          <li className="relative">
            <div className="flex items-start">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${getStepStatus(1)}`}>
                <span className="text-xs font-medium">2</span>
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="text-blue-500 text-base" />
                  Job Execution & Tracking
                </h3>
                
                {offer.overallActiveStep >= 1 && (
                  <div className="space-y-3">
                    
                    {/* Shifts Card */}
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <h4 className="text-sm font-medium text-gray-900">Shift Schedule & Attendance</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-in</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-out</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {offer.shifts.map((shift, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                                  {new Date(shift.date).toLocaleDateString()}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                  {new Date(shift.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(shift.endTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    shift.checkIn.confirmed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {shift.checkIn.confirmed
                                      ? `✓ ${new Date(shift.checkIn.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                                      : "Pending"}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    shift.checkOut.confirmed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {shift.checkOut.confirmed
                                      ? `✓ ${new Date(shift.checkOut.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                                      : "Pending"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Dispute Cards */}
                    {offer.status === "disputed" && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <AlertCircle className="text-orange-500 mr-2 text-base" />
                          <h4 className="text-sm font-medium text-orange-800">Active Dispute</h4>
                        </div>
                        
                        {offer.dispute?.definition && (
                          <div className="mb-3 p-3 bg-orange-100 rounded-lg">
                            <p className="text-xs font-medium text-orange-800">Current Issue:</p>
                            <p className="text-orange-700 text-xs">{offer.dispute.definition}</p>
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row gap-2">
                          <select
                            value={dispute.status || offer.dispute?.status || ''}
                            onChange={(e) => handleDisputeChange(parseInt(e.target.value))}
                            className="form-select text-xs max-w-xs border-orange-300 focus:border-orange-500 focus:ring-orange-200"
                          >
                            <option value="" disabled>Select Resolution Reason</option>
                            {disputeReasons.map((reason) => (
                              <option key={reason.status} value={reason.status}>
                                {reason.definition}
                              </option>
                            ))}
                          </select>
                          <button
                            className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            onClick={resolveDispute}
                            disabled={!dispute.status && !offer.dispute?.status}
                          >
                            Resolve Dispute
                          </button>
                        </div>
                      </div>
                    )}

                    {offer.status === "resolved" && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <CheckCircle className="text-green-500 mr-2 text-base" />
                          <h4 className="text-sm font-medium text-green-800">Dispute Resolved</h4>
                        </div>
                        <div className="space-y-1 text-xs">
                          <p><strong>Resolution:</strong> {offer.dispute?.definition || 'Dispute resolved successfully'}</p>
                          {offer.dispute?.resolvedAt && (
                            <p><strong>Resolved:</strong> {new Date(offer.dispute.resolvedAt).toLocaleString()}</p>
                          )}
                          {offer.dispute?.resolutionNotes && (
                            <p><strong>Notes:</strong> {offer.dispute.resolutionNotes}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Connecting line */}
            <div className="absolute left-4 top-8 -ml-px mt-0.5 h-full w-0.5 bg-gray-300" aria-hidden="true"></div>
          </li>

          {/* Step 3: Payment */}
          <li className="relative">
            <div className="flex items-start">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${getStepStatus(2)}`}>
                <span className="text-xs font-medium">3</span>
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CreditCard className="text-green-500 text-base" />
                  Payment & Billing
                </h3>
                
                {offer.overallActiveStep >= 2 && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="p-4">
                      {offer.invoice?.status === "paid" ? (
                        <div className="text-center">
                          <CheckCircle className="text-green-500 text-2xl mb-2 mx-auto" />
                          <h4 className="text-base font-semibold text-green-800 mb-2">Payment Completed</h4>
                          <div className="space-y-1 text-xs text-green-700">
                            {offer.invoice.amountTotal && (
                              <p><strong>Amount:</strong> ${offer.invoice.amountTotal}</p>
                            )}
                            <p><strong>Status:</strong> <span className="capitalize">{offer.invoice.status}</span></p>
                            {offer.invoice.url && (
                              <div className="mt-3">
                                <a
                                  href={offer.invoice.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  View Receipt →
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Clock className="text-yellow-500 text-2xl mb-2 mx-auto" />
                          <h4 className="text-base font-semibold text-yellow-800 mb-2">Invoice Issued</h4>
                          <div className="space-y-1 text-xs text-yellow-700">
                            <p><strong>Status:</strong> <span className="capitalize">{offer.invoice?.status || 'Pending'}</span></p>
                            {offer.invoice?.amountTotal && (
                              <p><strong>Amount:</strong> ${offer.invoice.amountTotal}</p>
                            )}
                            {offer.invoice?.dueDate && (
                              <p><strong>Due Date:</strong> {new Date(offer.invoice.dueDate).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </li>
        </ol>
      </nav>
    </div>
  );
};

export default WorkflowStepper;
